// Miroir des badges d'état de l'admin Shopify.
//
// Les valeurs viennent telles quelles de la synchro (GetOrderAction) :
// `financial_status` et `fulfillment_status` sont les enums `displayFinancialStatus`
// et `displayFulfillmentStatus` passés en minuscules, `UNFULFILLED` étant stocké
// à NULL. On reproduit la logique d'affichage de Shopify : le libellé, le ton et
// la pastille de progression Polaris (vide / à moitié pleine / pleine).

const FINANCIAL_STATUSES = {
    pending: {
        label: 'En attente de paiement',
        tone: 'attention',
        progress: 'incomplete',
    },
    authorized: {
        label: 'Autorisé',
        tone: 'attention',
        progress: 'partiallyComplete',
    },
    partially_paid: {
        label: 'Partiellement payé',
        tone: 'warning',
        progress: 'partiallyComplete',
    },
    paid: { label: 'Payé', tone: 'default', progress: 'complete' },
    partially_refunded: {
        label: 'Partiellement remboursé',
        tone: 'default',
        progress: 'complete',
    },
    refunded: { label: 'Remboursé', tone: 'default', progress: 'complete' },
    // Shopify dit « Annulé » — on précise « paiement » pour ne pas le confondre
    // avec l'annulation de la commande elle-même, qui est un autre badge.
    voided: {
        label: 'Paiement annulé',
        tone: 'default',
        progress: 'complete',
    },
    expired: { label: 'Expiré', tone: 'critical', progress: 'incomplete' },
}

const FULFILLMENT_STATUSES = {
    unfulfilled: {
        label: 'Non traitée',
        tone: 'attention',
        progress: 'incomplete',
    },
    partially_fulfilled: {
        label: 'Partiellement traitée',
        tone: 'warning',
        progress: 'partiallyComplete',
    },
    fulfilled: { label: 'Traitée', tone: 'default', progress: 'complete' },
    restocked: {
        label: 'Remise en stock',
        tone: 'default',
        progress: 'complete',
    },
    pending_fulfillment: {
        label: 'En attente de traitement',
        tone: 'attention',
        progress: 'incomplete',
    },
    open: { label: 'Ouverte', tone: 'attention', progress: 'incomplete' },
    in_progress: {
        label: 'En cours',
        tone: 'attention',
        progress: 'partiallyComplete',
    },
    on_hold: { label: 'En attente', tone: 'warning', progress: 'incomplete' },
    scheduled: { label: 'Planifiée', tone: 'attention', progress: 'incomplete' },
    request_declined: {
        label: 'Demande refusée',
        tone: 'warning',
        progress: 'complete',
    },
}

// Enum OrderCancelReason, stocké tel quel (en majuscules) par la synchro.
export const SHOPIFY_CANCEL_REASONS = {
    CUSTOMER: 'Demande du client',
    DECLINED: 'Paiement refusé',
    FRAUD: 'Commande frauduleuse',
    INVENTORY: 'Stock insuffisant',
    STAFF: "Erreur de l'équipe",
    OTHER: 'Autre motif',
}

/**
 * Une commande annulée, archivée ou remboursée est tranchée : plus rien n'est
 * en attente d'une décision de l'atelier.
 *
 * Sert à éteindre l'alerte « annulation demandée », pas à résoudre la demande —
 * `cancellation_resolved_at` reste vide exprès, la demande doit rester comptée
 * dans les stats. Le remboursement est le cas courant : l'équipe rembourse et
 * ferme la préparation sans passer par « Cancel order » côté Shopify, donc
 * `cancelled_at` ne suffit pas à détecter qu'une demande a été honorée.
 *
 * A shipped order settles too: the parcel is gone, so there is nothing left to
 * hold back. Mirrored server-side by `Order::getSettledAttribute` and, in SQL,
 * by `Order::scopeCancellationPending` — the three move together.
 */
export function isOrderSettled(order) {
    return Boolean(
        order?.cancelled_at ||
            order?.closed_at ||
            order?.financial_status === 'refunded' ||
            order?.fulfillment_status
    )
}

const TONE_CLASSES = {
    default: 'bg-gray-100 text-gray-700',
    attention: 'bg-yellow-100 text-yellow-900',
    warning: 'bg-orange-100 text-orange-900',
    critical: 'bg-red-100 text-red-900',
}

// Pastille de progression Polaris : cercle vide, à moitié plein, ou plein.
function ProgressDot({ progress }) {
    return (
        <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            aria-hidden="true"
            className="shrink-0 opacity-80"
        >
            <circle
                cx="4"
                cy="4"
                r="3.25"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
            />

            {progress === 'complete' && (
                <circle cx="4" cy="4" r="3.25" fill="currentColor" />
            )}

            {progress === 'partiallyComplete' && (
                <path d="M4 0.75 A3.25 3.25 0 0 1 4 7.25 Z" fill="currentColor" />
            )}
        </svg>
    )
}

function StatusBadge({ label, tone, progress }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
        >
            <ProgressDot progress={progress} />
            {label}
        </span>
    )
}

export default function OrderStatusBadges({ order }) {
    const badges = []

    if (order.cancelled_at) {
        badges.push({
            key: 'cancelled',
            label: 'Annulée',
            tone: 'critical',
            progress: 'complete',
        })
    }

    const financial = FINANCIAL_STATUSES[order.financial_status]

    if (financial) {
        badges.push({ key: 'financial', ...financial })
    } else if (order.financial_status) {
        // Un enum que Shopify aurait ajouté depuis : on l'affiche brut plutôt
        // que de le taire.
        badges.push({
            key: 'financial',
            label: order.financial_status,
            tone: 'default',
            progress: 'incomplete',
        })
    }

    // Shopify n'affiche pas d'état de traitement sur une commande annulée :
    // les articles sont sortis des préparations.
    if (!order.cancelled_at) {
        const fulfillment =
            FULFILLMENT_STATUSES[order.fulfillment_status ?? 'unfulfilled']

        badges.push({
            key: 'fulfillment',
            ...(fulfillment ?? {
                label: order.fulfillment_status,
                tone: 'default',
                progress: 'incomplete',
            }),
        })
    }

    // Shopify archive automatiquement les commandes annulées : le redire
    // n'apprendrait rien.
    if (order.closed_at && !order.cancelled_at) {
        badges.push({
            key: 'archived',
            label: 'Archivée',
            tone: 'default',
            progress: 'complete',
        })
    }

    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {badges.map(({ key, ...badge }) => (
                <StatusBadge key={key} {...badge} />
            ))}
        </div>
    )
}
